import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Divider,
  Input,
  Popover,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { authFetch } from '../../utils/api';
import { getBrandLabel, tokenManager } from '../../utils/tokenManager';
import { useBrandOptions } from '../../hooks/useBrandOptions';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import {
  countDrift,
  evaluateCell,
  isPerUserMeteringEnabled,
  isSharedAllBrands,
  normalizeAllowedBrands,
  type CellStatus,
  type MatrixNode,
  type MatrixTier,
} from './effective';
import { orchestrateNodeAvailability } from './api';

const { Title, Paragraph, Text } = Typography;

/**
 * 節點 × 品牌可用性矩陣（P1 唯讀）
 *
 * 設計依據：docs/operations/backoffice-redesign-2026-06-27.md §5.4 / §5.6 / §7。
 *   列＝節點（含 allowedBrands tag）、欄＝品牌（動態目錄驅動 kailian/kshield/wudi_star…）、
 *   每格＝該節點對該品牌方案在 APP 端的綜合可用性狀態燈（鏡像 /api/ip gate）。
 *
 * 🔴 紅線（已在 effective.ts 落實）：
 *   - 品牌欄一律動態目錄驅動（useBrandOptions → /api/admin/brands），不硬編。
 *   - allowedBrands fail-OPEN：NULL/空＝全品牌共享，所有品牌欄顯示「🌐 全品牌共享」。
 *   - 狀態涵蓋多 gate（可用/可見未選/品牌不可見/漂移/全品牌共享/其他 gate），不只三態。
 *   - P1 唯讀：點格只開唯讀 Popover 看 why-not，**不做任何寫入 / toggle**。
 */

/** 狀態燈視覺（對齊 §7.2 六色 + mockup legend）。 */
const STATUS_META: Record<
  CellStatus,
  { label: string; emoji: string; dot: string; text: string }
> = {
  ok: { label: '可用', emoji: '✅', dot: '#16a34a', text: '#15803d' },
  unsel: { label: '可見未選', emoji: '🟡', dot: '#d97706', text: '#b45309' },
  invisible: { label: '不可見', emoji: '⛔', dot: '#94a3b8', text: '#64748b' },
  drift: { label: '漂移', emoji: '⚠️', dot: '#dc2626', text: '#b91c1c' },
  shared: {
    label: '全品牌共享',
    emoji: '🌐',
    dot: 'linear-gradient(135deg,#0e7490,#2563eb,#7c3aed)',
    text: '#1d4ed8',
  },
  gate: { label: '其他 gate 擋', emoji: '⬛', dot: '#334155', text: '#334155' },
};

const LEGEND_ORDER: CellStatus[] = [
  'ok',
  'unsel',
  'invisible',
  'drift',
  'shared',
  'gate',
];

interface RawNode {
  id: number;
  name: string;
  displayName?: string | null;
  region?: string;
  ip?: string;
  port?: number;
  nodeType?: 'sk6' | 'vless';
  isDecoy?: boolean;
  isEnabled?: boolean;
  healthStatus?: string;
  minTier?: number;
  allowedBrands?: string[] | null;
  config?: string | null;
}

interface RawTier {
  id: number;
  name: string;
  level: number;
  brand: string;
  canAccessVless?: boolean;
  canAccessDaita?: boolean;
  allowedNodeIds?: number[] | null;
}

function toMatrixNode(n: RawNode): MatrixNode {
  return {
    id: n.id,
    name: n.name,
    displayName: n.displayName,
    region: n.region,
    ip: n.ip,
    port: n.port,
    nodeType: n.nodeType,
    isDecoy: n.isDecoy,
    isEnabled: n.isEnabled,
    healthStatus: n.healthStatus,
    minTier: n.minTier,
    allowedBrands: n.allowedBrands,
    config: n.config,
  };
}

/** 節點列的 allowedBrands tag：fail-OPEN → 🌐 全品牌共享；否則列出品牌標籤。 */
function NodeBrandTags({ node }: { node: MatrixNode }) {
  if (isSharedAllBrands(node.allowedBrands)) {
    return <Tag color="cyan">🌐 全品牌共享</Tag>;
  }
  const brands = normalizeAllowedBrands(node.allowedBrands) ?? [];
  return (
    <>
      {brands.map((b) => (
        <Tag key={b} color="blue">
          {getBrandLabel(b)}
        </Tag>
      ))}
    </>
  );
}

/** 唯讀 why-not Popover 內容（品牌 admin 視角 / 不可寫時）。 */
function CellReadonlyContent({
  node,
  brand,
  cell,
}: {
  node: MatrixNode;
  brand: string;
  cell: ReturnType<typeof evaluateCell>;
}) {
  const meta = STATUS_META[cell.status];
  return (
    <div style={{ maxWidth: 320 }}>
      <div style={{ marginBottom: 6 }}>
        <Text strong>
          {meta.emoji} {meta.label}
        </Text>{' '}
        <Text type="secondary">
          （{getBrandLabel(brand)} / {node.displayName || node.name}）
        </Text>
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {cell.reasons.map((r, i) => (
          <li key={i}>
            <Text style={{ fontSize: 12 }}>{r}</Text>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 8 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          共享基礎設施唯讀檢視。任何寫入 / 跨品牌曝光須由全域管理員操作（需顯式 targetBrand +
          二次確認）。
        </Text>
      </div>
    </div>
  );
}

/**
 * 寫入 Popover 內容（全域 admin only，§5.4 / §7.3 screen 2 雙正交開關）。
 *
 * 兩個正交開關：
 *   1. 對此品牌可見（節點 allowedBrands，純新增）— 對應 grantBrandVisibility。
 *   2. 加入此品牌方案白名單（allowed_node_ids）— 對應 addToTierIds。
 *
 * fail-safe：真正 enforce 在後端（default-deny + 純擴大 + TOCTOU 重算）。前端：
 *   - ⛔/⚠️（對 targetBrand 不可見）要曝光 → 先送無旗標，後端回 blocked envelope →
 *     紅色二次確認 → 帶 confirmExpandBrandExposure=true 重送。**不預設帶旗標**。
 *   - 套用後呼叫 onApplied 重抓，狀態燈即時反映真實 effective。
 */
function CellWriteContent({
  node,
  brand,
  cell,
  brandTiers,
  onApplied,
  onClose,
}: {
  node: MatrixNode;
  brand: string;
  cell: ReturnType<typeof evaluateCell>;
  brandTiers: MatrixTier[];
  onApplied: () => void;
  onClose: () => void;
}) {
  // React 19 下 antd 靜態 message.* / Modal.confirm 走 legacy ReactDOM.render 不渲染
  // → 用 App context 綁定的 message / modal（App.tsx 已用 <AntdApp> 包裹整棵樹）。
  const { message, modal } = AntdApp.useApp();
  const meta = STATUS_META[cell.status];
  const currentlyVisible = cell.status !== 'invisible' && cell.status !== 'drift';
  // 預設勾選：可見開關反映現況（共享/已可見 → 勾）；方案開關預設不勾（避免誤把節點塞進方案）。
  const [grantVisibility, setGrantVisibility] = useState(!currentlyVisible);
  const qualifying = new Set(cell.qualifyingTierIds);
  const [selectedTierIds, setSelectedTierIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const tierOptions = brandTiers.map((t) => ({
    label: `${t.name}（level ${t.level}）${qualifying.has(t.id) ? '・已選' : ''}`,
    value: t.id,
    disabled: qualifying.has(t.id),
  }));

  const fire = useCallback(
    async (confirmExpand: boolean) => {
      setSubmitting(true);
      try {
        const resp = await orchestrateNodeAvailability({
          nodeIds: [node.id],
          targetBrand: brand,
          addToTierIds: selectedTierIds,
          grantBrandVisibility: grantVisibility,
          confirmExpandBrandExposure: confirmExpand,
        });

        // default-deny 擋下 → 紅色二次確認後帶旗標重送。
        if (
          resp.code !== 0 &&
          resp.data?.blocked?.reason === 'CROSS_BRAND_EXPOSURE_REQUIRES_CONFIRM'
        ) {
          const blocked = resp.data.blocked;
          modal.confirm({
            title: '擴大品牌曝光確認',
            okText: '我了解，確認開放',
            cancelText: '取消',
            okButtonProps: { danger: true },
            content: (
              <div style={{ fontSize: 13 }}>
                <p style={{ marginTop: 0 }}>
                  即將把下列節點對 <strong>{getBrandLabel(brand)}</strong>{' '}
                  開放可見性（純新增，新集合 ⊇ 舊集合）。此為安全決定，將寫入稽核：
                </p>
                <ul style={{ paddingLeft: 18 }}>
                  {blocked.invisibleNodes.map((n) => (
                    <li key={n.id}>
                      {n.name}（目前可見品牌：
                      {n.allowedBrands && n.allowedBrands.length > 0
                        ? n.allowedBrands.join('、')
                        : '全品牌共享'}
                      ）
                    </li>
                  ))}
                </ul>
              </div>
            ),
            onOk: async () => {
              await fire(true);
            },
          });
          return;
        }

        if (resp.code !== 0) {
          message.error(resp.msg || '節點可用性更新失敗');
          return;
        }

        message.success('節點可用性已更新');
        onClose();
        onApplied();
      } catch (e) {
        message.error(e instanceof Error ? e.message : '節點可用性更新失敗');
      } finally {
        setSubmitting(false);
      }
    },
    [node.id, brand, selectedTierIds, grantVisibility, onApplied, onClose, message, modal]
  );

  const nothingToDo = !grantVisibility && selectedTierIds.length === 0;

  return (
    <div style={{ maxWidth: 360 }}>
      <div style={{ marginBottom: 6 }}>
        <Text strong>
          {meta.emoji} {meta.label}
        </Text>{' '}
        <Text type="secondary">
          （{getBrandLabel(brand)} / {node.displayName || node.name}）
        </Text>
      </div>
      <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
        {cell.reasons.map((r, i) => (
          <li key={i}>
            <Text style={{ fontSize: 12 }}>{r}</Text>
          </li>
        ))}
      </ul>

      <Divider style={{ margin: '8px 0' }} />

      <div style={{ marginBottom: 10 }}>
        <Checkbox
          checked={grantVisibility}
          onChange={(e) => setGrantVisibility(e.target.checked)}
        >
          <Text strong style={{ fontSize: 13 }}>
            對此品牌可見（節點 allowedBrands）
          </Text>
        </Checkbox>
        <div style={{ fontSize: 11, color: '#94a3b8', paddingLeft: 24 }}>
          {cell.shared
            ? '此節點目前全品牌共享(NULL)；勾選＝顯式把此品牌加進清單（不影響其他品牌）。'
            : `把 ${getBrandLabel(brand)} 純新增進 allowedBrands；後端 tx 內重算 ⊇，拒偽裝縮減。`}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <Text strong style={{ fontSize: 13 }}>
          加入此品牌方案白名單
        </Text>
        <Select
          mode="multiple"
          allowClear
          size="small"
          style={{ width: '100%', marginTop: 4 }}
          placeholder="選擇要加入此節點的方案（可不選）"
          value={selectedTierIds}
          onChange={setSelectedTierIds}
          options={tierOptions}
        />
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          委派既有 updateTier 核心（normalizeAllowedNodeIds + confirmDenyAll + DAITA 稽核）。
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 8,
        }}
      >
        <Button size="small" onClick={onClose} disabled={submitting}>
          取消
        </Button>
        <Button
          size="small"
          type="primary"
          loading={submitting}
          disabled={nothingToDo}
          onClick={() => fire(false)}
        >
          套用
        </Button>
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
        對此品牌不可見的節點要曝光時，後端 default-deny → 會跳紅色二次確認。fail-OPEN
        節點(NULL)不會被本動作收窄。
      </div>
    </div>
  );
}

/** 單格狀態燈：dot + emoji + 簡短文字，Popover 展開 why-not（唯讀）或雙開關（全域 admin）。 */
function CellLamp({
  node,
  brand,
  brandTiers,
  canWrite,
  onApplied,
}: {
  node: MatrixNode;
  brand: string;
  brandTiers: MatrixTier[];
  canWrite: boolean;
  onApplied: () => void;
}) {
  const [open, setOpen] = useState(false);
  const cell = evaluateCell(node, brandTiers, brand);
  const meta = STATUS_META[cell.status];

  const content = canWrite ? (
    <CellWriteContent
      node={node}
      brand={brand}
      cell={cell}
      brandTiers={brandTiers}
      onApplied={onApplied}
      onClose={() => setOpen(false)}
    />
  ) : (
    <CellReadonlyContent node={node} brand={brand} cell={cell} />
  );

  return (
    <Popover
      content={content}
      title={null}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      destroyTooltipOnHide
    >
      <span
        role="button"
        tabIndex={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          fontSize: 12,
          color: meta.text,
          fontWeight: cell.status === 'ok' || cell.status === 'shared' ? 600 : 500,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: meta.dot,
            flex: '0 0 auto',
          }}
        />
        {meta.emoji} {meta.label}
      </span>
    </Popover>
  );
}

export const NodeBrandMatrixPage = () => {
  const [nodes, setNodes] = useState<MatrixNode[]>([]);
  const [tiers, setTiers] = useState<MatrixTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');

  const brandOptions = useBrandOptions();
  const brands = useMemo(() => brandOptions.map((o) => o.value), [brandOptions]);
  // 共享基礎設施寫入只開放給全域 admin（§4.2）；品牌 admin 維持唯讀 why-not Popover。
  const canWrite = tokenManager.hasToken() && tokenManager.isAllBrandAdmin();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nodesRes, tiersRes] = await Promise.all([
        // pageSize 上限 100（後端 OOM 根治的分頁 cap）；節點實際僅 ~50 個，一頁裝完。
        authFetch('/api/admin/nodes?page=1&pageSize=100'),
        authFetch('/api/admin/tiers'),
      ]);
      const nodesJson = await nodesRes.json();
      const tiersJson = await tiersRes.json();

      if (nodesJson.code !== 0 || tiersJson.code !== 0) {
        throw new Error(
          nodesJson.msg || tiersJson.msg || '讀取節點 / 等級資料失敗'
        );
      }

      const rawNodes: RawNode[] = Array.isArray(nodesJson.data?.list)
        ? nodesJson.data.list
        : [];
      const rawTiers: RawTier[] = Array.isArray(tiersJson.data)
        ? tiersJson.data
        : [];

      setNodes(rawNodes.map(toMatrixNode));
      setTiers(
        rawTiers.map((t) => ({
          id: t.id,
          name: t.name,
          level: t.level,
          brand: t.brand,
          canAccessVless: t.canAccessVless,
          canAccessDaita: t.canAccessDaita,
          allowedNodeIds: t.allowedNodeIds,
        }))
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '讀取資料失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const tiersByBrand = useMemo(() => {
    const map = new Map<string, MatrixTier[]>();
    for (const t of tiers) {
      const arr = map.get(t.brand) ?? [];
      arr.push(t);
      map.set(t.brand, arr);
    }
    return map;
  }, [tiers]);

  const filteredNodes = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return nodes;
    return nodes.filter((n) =>
      [n.name, n.displayName, n.ip, n.region]
        .filter((v): v is string => typeof v === 'string')
        .some((v) => v.toLowerCase().includes(kw))
    );
  }, [nodes, keyword]);

  const driftCount = useMemo(
    () => countDrift(nodes, tiersByBrand, brands),
    [nodes, tiersByBrand, brands]
  );

  const columns: ColumnsType<MatrixNode> = useMemo(() => {
    const nodeCol: ColumnsType<MatrixNode>[number] = {
      title: '節點',
      key: 'node',
      fixed: 'left',
      width: 260,
      render: (_: unknown, node: MatrixNode) => (
        <div>
          <div style={{ fontWeight: 600 }}>{node.displayName || node.name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {[node.region, node.ip].filter(Boolean).join(' · ')}
            {(node.nodeType ?? 'sk6') === 'vless' ? ' · VLESS' : ''}
            {typeof node.minTier === 'number' && node.minTier > 0
              ? ` · minTier=${node.minTier}`
              : ''}
          </div>
          <div style={{ marginTop: 4 }}>
            <Space size={4} wrap>
              <NodeBrandTags node={node} />
              {isPerUserMeteringEnabled(node) ? (
                <Tag color="purple">per-user 計量</Tag>
              ) : null}
            </Space>
          </div>
        </div>
      ),
    };

    const brandCols: ColumnsType<MatrixNode> = brands.map((brand) => ({
      title: (
        <Space size={6}>
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#2563eb',
              display: 'inline-block',
            }}
          />
          {getBrandLabel(brand)}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {brand}
          </Text>
        </Space>
      ),
      key: brand,
      align: 'center',
      width: 150,
      render: (_: unknown, node: MatrixNode) => (
        <CellLamp
          node={node}
          brand={brand}
          brandTiers={tiersByBrand.get(brand) ?? []}
          canWrite={canWrite}
          onApplied={fetchData}
        />
      ),
    }));

    return [nodeCol, ...brandCols];
  }, [brands, tiersByBrand, canWrite, fetchData]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          節點 × 品牌可用性矩陣
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          單一視圖檢視每個節點對各品牌（可下鑽方案）在 APP 端的真實綜合可用性，根治
          VPN-80「兩個正交開關各設 + 靜默成功的失敗」。每格鏡像 APP{' '}
          <Text code>/api/ip</Text> 的下發 gate。
          {canWrite ? (
            <>
              {' '}
              全域管理員可點任一格在同一彈窗操作兩個正交開關（節點對品牌可見 + 加入方案白名單），
              經編排端點原子寫入；對品牌不可見的節點要曝光時後端 default-deny → 跳紅色二次確認。
            </>
          ) : (
            <>{' '}共享基礎設施唯讀；修改請由全域管理員操作。</>
          )}
        </Paragraph>
      </Card>

      <BrandScopeNotice kind="shared-infra" />

      {driftCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`偵測到 ${driftCount} 筆可見性漂移`}
          description={
            <span>
              節點在方案白名單內，但對該品牌不可見（已用{' '}
              <Text code>isBrandAllowed()</Text>{' '}
              判定，全品牌共享節點不算漂移）→ APP 仍看不到。
              {canWrite
                ? '點該格在彈窗勾「對此品牌可見」即可修正（只把品牌加進節點可見性、不收窄 NULL 共享，經二次確認 + audit）。'
                : '修正須由全域管理員操作。'}
            </span>
          }
        />
      ) : null}

      <Card
        title={
          <Space>
            <span>可用性矩陣</span>
            <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>
              {nodes.length} 節點 × {brands.length} 品牌
            </Text>
          </Space>
        }
        extra={
          <Input.Search
            allowClear
            placeholder="搜尋節點名稱 / IP / 地區"
            style={{ width: 240 }}
            onChange={(e) => setKeyword(e.target.value)}
          />
        }
      >
        {error ? (
          <Alert
            type="error"
            showIcon
            message="讀取失敗"
            description={error}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <Spin spinning={loading}>
          <Table<MatrixNode>
            rowKey="id"
            size="small"
            columns={columns}
            dataSource={filteredNodes}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100'],
              showTotal: (t) => `共 ${t} 個節點`,
            }}
          />
        </Spin>

        <div style={{ marginTop: 12 }}>
          <Space size={16} wrap>
            {LEGEND_ORDER.map((s) => {
              const meta = STATUS_META[s];
              return (
                <span
                  key={s}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: '#475569',
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: meta.dot,
                      display: 'inline-block',
                    }}
                  />
                  {meta.emoji} {meta.label}
                </span>
              );
            })}
          </Space>
        </div>

        <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
          <Text strong>fail-OPEN 提醒：</Text>節點{' '}
          <Text code>allowedBrands</Text> 為 NULL/空＝對全部品牌可見（🌐
          全品牌共享）；本頁的「對此品牌可見」只做純新增、絕不把 NULL 共享收窄成單一品牌。
          點任一格可展開該格在 APP 端被下發 / 擋下的完整 why-not
          {canWrite ? '，全域管理員可在彈窗就地操作兩個正交開關' : ''}。
        </Paragraph>
      </Card>
    </Space>
  );
};

export default NodeBrandMatrixPage;
